import { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import { Box, Grid, InternalStandardProps } from "@mui/material";
import icEvent from "../../../Images/Content/ic_event.gif";
import { messaging } from "JS/Helpers/UserMessaging";
import { AppBackdropProgress } from "./Progress/AppBackdropProgress";

const Root = styled(Grid)(({ theme }) => ({
    justifyContent: "center",
    marginTop: theme.spacing(2),
}));

const RootWithPadding = styled(Root)(({ theme }) => ({
    paddingLeft: theme.spacing(2),
}));

const BackgroundImageContainer = styled(Grid)(() => ({
    display: "flex",
    justifyContent: "center",
    marginTop: "2%",
    marginBottom: "3%",
}));

const BackImageStyle = styled("img")(({ theme }) => ({
    maxWidth: "300px",
    maxHeight: "350px",
    objectFit: "contain",
    [theme.breakpoints.up("md")]: {
        maxWidth: "450px",
        maxHeight: "450px",
    },
}));

const ImageBoxContainer = styled(Box)(({ theme }) => ({
    position: "relative",
    background: theme.palette.grey["100"],
    padding: theme.spacing(2),
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
}));

const LtdGif = styled("img")(({ theme }) => ({
    background: "white",
    position: "absolute",
    width: "80px",
    height: "80px",
    border: `4px solid ${theme.palette.grey[500]}`,
    borderRadius: "20%",
    right: "-10px",
    marginRight: "10px",
    top: "-35px",
}));

export interface userImageProps
    extends InternalStandardProps<
        React.DetailedHTMLProps<
            React.HTMLAttributes<HTMLDivElement>,
            HTMLDivElement
        >
    > {
    isImageExists: boolean;
    backgroundImageUrl: string;
    handleUploadClick: () => void;
    isDefaultBehaviour: boolean;
    setDialogs: React.Dispatch<
        React.SetStateAction<{
            uploadImageDialog: boolean;
            webCamImageDialog: boolean;
        }>
    >;
    dialogs: {
        uploadImageDialog: boolean;
        webCamImageDialog: boolean;
    };
    handleUploadImage: (event: any) => void;
    hdnFileInput: any;
    fetchingImage: boolean;
    isFromFYC?: boolean;
}

export const UserImage = (props: userImageProps) => {
    const {
        isImageExists,
        backgroundImageUrl,
        handleUploadClick,
        isDefaultBehaviour,
        setDialogs,
        dialogs,
        handleUploadImage,
        hdnFileInput,
        fetchingImage,
        isFromFYC,
    } = props;
    const [imageLoading, setImageLoading] = useState(false);

    useEffect(() => {
        if (fetchingImage) {
            setImageLoading(true);
        } else if (!isImageExists) {
            setImageLoading(false);
        }
    }, [fetchingImage, isImageExists]);

    useEffect(() => {
        if (isImageExists && backgroundImageUrl) {
            setImageLoading(true);
        }
    }, [isImageExists, backgroundImageUrl]);

    const showSpinner = fetchingImage || imageLoading;

    const RootComponent = isFromFYC ? RootWithPadding : Root;

    return (
        <>
            <RootComponent container>
                <BackgroundImageContainer item xs={12}>
                    {showSpinner && (
                        <AppBackdropProgress
                            open={showSpinner}
                            backdropText={messaging?.events?.fetchingPhoto}
                        />
                    )}
                    {isImageExists && backgroundImageUrl && (
                        <ImageBoxContainer>
                            <LtdGif src={icEvent} alt="Not-Found" />
                            <BackImageStyle
                                src={backgroundImageUrl}
                                alt="Not-Found"
                                onLoad={() => setImageLoading(false)}
                                onError={() => setImageLoading(false)}
                                onClick={
                                    isDefaultBehaviour
                                        ? () =>
                                              setDialogs({
                                                  ...dialogs,
                                                  uploadImageDialog: true,
                                              })
                                        : () => handleUploadClick()
                                }
                            />
                        </ImageBoxContainer>
                    )}
                </BackgroundImageContainer>
            </RootComponent>
            {!isDefaultBehaviour && (
                <input
                    style={{ display: "none" }}
                    type="file"
                    ref={hdnFileInput}
                    data-testid="imageUpload"
                    accept="image/png, image/gif, image/jpeg"
                    id="imageUpload"
                    onChange={(event) => handleUploadImage(event)}
                />
            )}
        </>
    );
};
